import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { CircleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { CloneStepPreview, JourneyCase, StepCloneOutcome } from "../dtos";
import { useCreateStepClones, useCloneSourcePreview, useCloneTargetPreview } from "../hooks/use-clone-steps.hook";
import { useStartWorkflow } from "../hooks/use-start-workflow.hook";
import {
  applicationLabel,
  cloneFromForStart,
  defaultSelectedTemplateStepIds,
  groupPreviewByPhase,
  ineligibleReasonCopy,
  isCloneOverwriteRequired,
  iscoreNoteFor,
  needsOverwriteConfirm,
  otherApplications,
  outcomeLabel,
} from "../utils/clone-steps";
import { primaryButtonSx, secondaryButtonSx } from "./field-shell";

type CloneStartOption = "empty" | "copy";
type CloneMode = "start" | "copy";
type SemanticColors = Theme["nawy"]["colors"]["semantic"];

const labelSx = (colors: SemanticColors) =>
  ({
    fontSize: 11,
    lineHeight: "16px",
    color: colors.text.secondary,
    mb: 0.5,
  }) as const;

const phaseHeaderSx = (colors: SemanticColors) =>
  ({
    fontSize: 11,
    lineHeight: "16px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: colors.text.secondary,
    mt: 1,
    mb: 0.5,
  }) as const;

/** Result colour per outcome: filled → positive, still waiting → neutral, anything else → negative. */
const outcomeTone = (
  status: StepCloneOutcome["status"],
  colors: SemanticColors,
): SemanticColors["text"]["secondary"] => {
  if (status === "APPLIED") return colors.text.extension.positive;
  if (status === "PENDING") return colors.text.secondary;
  return colors.text.extension.negative;
};

/** One cloneable step in the checklist: checkbox, step name and its reason / overwrite / i-Score notes. */
const CloneStepRow = ({
  row,
  mode,
  checked,
  disabled,
  onToggle,
}: Readonly<{
  row: CloneStepPreview;
  mode: CloneMode;
  checked: boolean;
  disabled: boolean;
  onToggle: (templateStepId: number) => void;
}>) => {
  const colors = useTheme().nawy.colors.semantic;
  const reasonCopy = ineligibleReasonCopy(row.reason);
  const note = iscoreNoteFor(row.iscore);
  const overwritesThisRow = mode === "copy" && row.eligible && row.targetHasData;
  const noteColor = note?.warn ? colors.text.extension.negative : colors.text.secondary;
  return (
    <FormControlLabel
      sx={{
        display: "flex",
        alignItems: "flex-start",
        ml: 0,
        mb: 0.25,
        "& .MuiCheckbox-root": { pt: "2px" },
      }}
      control={
        <Checkbox
          size="small"
          disabled={!row.eligible || disabled}
          checked={checked}
          onChange={() => onToggle(row.templateStepId)}
        />
      }
      label={
        <Box>
          <Typography sx={{ fontSize: 14, color: colors.text.primary }}>{row.stepName}</Typography>
          {reasonCopy && <Typography sx={{ fontSize: 11, color: colors.text.secondary }}>{reasonCopy}</Typography>}
          {overwritesThisRow && (
            <Typography sx={{ fontSize: 11, color: colors.text.extension.negative }}>
              Overwrites existing data
            </Typography>
          )}
          {note && <Typography sx={{ fontSize: 11, color: noteColor }}>{note.text}</Typography>}
        </Box>
      }
    />
  );
};

/** The "I understand this overwrites…" band shown when a ticked step already holds data. */
const OverwriteConfirmBand = ({
  checked,
  disabled,
  onChange,
}: Readonly<{
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}>) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      sx={{
        mt: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: theme.nawy.radius.lg,
        backgroundColor: colors.layer.extension.negativeLight,
      }}
    >
      <CircleAlert size={16} color={colors.text.extension.negative} style={{ flexShrink: 0, marginTop: 3 }} />
      <FormControlLabel
        sx={{
          ml: 0,
          "& .MuiTypography-root": {
            fontSize: 12,
            color: colors.text.extension.negative,
          },
        }}
        control={
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
          />
        }
        label="I understand this overwrites existing data on the ticked steps. History keeps the old values."
      />
    </Stack>
  );
};

/** The source's cloneable steps, grouped by phase — or the loading / error / empty state. */
const CloneChecklist = ({
  isLoading,
  isError,
  onRetry,
  rows,
  mode,
  selected,
  busy,
  onToggle,
  overwriteNeeded,
  overwriteConfirmed,
  onOverwriteConfirmedChange,
}: Readonly<{
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  rows: CloneStepPreview[];
  mode: CloneMode;
  selected: Set<number>;
  busy: boolean;
  onToggle: (templateStepId: number) => void;
  overwriteNeeded: boolean;
  overwriteConfirmed: boolean;
  onOverwriteConfirmedChange: (checked: boolean) => void;
}>) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  }
  if (isError) {
    return (
      <Stack spacing={1} alignItems="center" sx={{ py: 2 }}>
        <Typography sx={{ fontSize: 13, color: colors.text.extension.negative }}>
          Couldn&apos;t load this application&apos;s cloneable steps.
        </Typography>
        <Button size="small" onClick={onRetry} sx={secondaryButtonSx(theme)}>
          Retry
        </Button>
      </Stack>
    );
  }
  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: colors.text.secondary, py: 1 }}>
        No cloneable steps with data on this application.
      </Typography>
    );
  }
  return (
    <Box>
      {groupPreviewByPhase(rows).map((group) => (
        <Box key={group.phaseName}>
          <Typography sx={phaseHeaderSx(colors)}>{group.phaseName}</Typography>
          {group.rows.map((row) => (
            <CloneStepRow
              key={row.templateStepId}
              row={row}
              mode={mode}
              checked={selected.has(row.templateStepId)}
              disabled={busy}
              onToggle={onToggle}
            />
          ))}
        </Box>
      ))}
      {overwriteNeeded && (
        <OverwriteConfirmBand checked={overwriteConfirmed} disabled={busy} onChange={onOverwriteConfirmedChange} />
      )}
    </Box>
  );
};

/** COPY mode's per-step outcome list, shown once `POST step-clones` resolves. */
const CloneResults = ({ results, rows }: Readonly<{ results: StepCloneOutcome[]; rows: CloneStepPreview[] }>) => {
  const colors = useTheme().nawy.colors.semantic;
  return (
    <Stack spacing={1}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.text.primary }}>Copy finished</Typography>
      {results.map((outcome) => {
        const sourceRow = rows.find((row) => row.templateStepId === outcome.targetTemplateStepId);
        return (
          <Stack
            key={outcome.cloneUid}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{
              py: 0.5,
              borderBottom: `1px solid ${colors.border.secondary}`,
            }}
          >
            <Typography sx={{ fontSize: 13, color: colors.text.primary }}>
              {sourceRow?.stepName ?? `Step ${outcome.targetTemplateStepId}`}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: outcomeTone(outcome.status, colors),
                textAlign: "right",
              }}
            >
              {outcomeLabel(outcome)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

/** START mode's "Start empty" / "Copy from…" choice. */
const StartOptionPicker = ({
  value,
  disabled,
  onChange,
}: Readonly<{
  value: CloneStartOption;
  disabled: boolean;
  onChange: (value: CloneStartOption) => void;
}>) => {
  const theme = useTheme();
  const isEmpty = value === "empty";
  return (
    <Box>
      <Typography sx={labelSx(theme.nawy.colors.semantic)}>How should this application start?</Typography>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant={isEmpty ? "contained" : "outlined"}
          disabled={disabled}
          onClick={() => onChange("empty")}
          sx={isEmpty ? primaryButtonSx(theme) : secondaryButtonSx(theme)}
        >
          Start empty
        </Button>
        <Button
          size="small"
          variant={isEmpty ? "outlined" : "contained"}
          disabled={disabled}
          onClick={() => onChange("copy")}
          sx={isEmpty ? secondaryButtonSx(theme) : primaryButtonSx(theme)}
        >
          Copy from…
        </Button>
      </Stack>
    </Box>
  );
};

/** The other applications of this lead to copy from, labelled "Application N" like `CaseSwitcher`. */
const SourcePicker = ({
  cases,
  candidates,
  value,
  disabled,
  onPick,
}: Readonly<{
  cases: JourneyCase[];
  candidates: JourneyCase[];
  value: string | null;
  disabled: boolean;
  onPick: (caseUid: string) => void;
}>) => {
  const colors = useTheme().nawy.colors.semantic;
  return (
    <Box>
      <Typography sx={labelSx(colors)}>Copy from</Typography>
      {candidates.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: colors.text.secondary }}>
          No other applications on this lead yet.
        </Typography>
      ) : (
        <RadioGroup aria-label="Copy from" value={value ?? ""} onChange={(event) => onPick(event.target.value)}>
          {candidates.map((row) => (
            <FormControlLabel
              key={row.uid}
              value={row.uid}
              disabled={disabled}
              control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: 14 }}>{applicationLabel(cases, row.uid)}</Typography>}
            />
          ))}
        </RadioGroup>
      )}
    </Box>
  );
};

/** Footer: "Done" once COPY results are showing, otherwise Cancel + the confirm button. */
const DialogFooter = ({
  showDone,
  busy,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  onCancel,
  onDone,
}: Readonly<{
  showDone: boolean;
  busy: boolean;
  confirmLabel: string;
  confirmDisabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onDone: () => void;
}>) => {
  const theme = useTheme();
  if (showDone) {
    return (
      <Button variant="contained" disableElevation onClick={onDone} sx={primaryButtonSx(theme)}>
        Done
      </Button>
    );
  }
  return (
    <>
      <Button disabled={busy} onClick={onCancel} sx={secondaryButtonSx(theme)}>
        Cancel
      </Button>
      <Button
        variant="contained"
        disableElevation
        disabled={confirmDisabled}
        startIcon={busy ? <CircularProgress size={13} color="inherit" /> : undefined}
        onClick={onConfirm}
        sx={primaryButtonSx(theme)}
      >
        {confirmLabel}
      </Button>
    </>
  );
};

/**
 * §Clone-steps: prefill an application's steps from another application on the SAME lead.
 *
 * Two modes, one dialog (both share the picker + checklist UI):
 * - `start`: from "Start another application" — offers "Start empty" or "Copy from…"; confirming
 *   creates the case via `useStartWorkflow`, attaching `cloneFrom` only when copying with at least
 *   one step ticked. WM plans the copy inside the SAME creation transaction — there is no separate
 *   per-step result to show here, just the usual "flow started" toast.
 * - `copy`: from "Copy data from another application" on a case that already exists — the target is
 *   fixed (the selected application); confirming calls `POST /v1/cases/:uid/step-clones` and this
 *   dialog renders the per-step outcome (APPLIED / PENDING / CONFLICT / …) before closing.
 */
const CloneStepsDialog = ({
  open,
  onClose,
  mode,
  cases,
  customerUid,
  leadUid,
  onStarted,
  targetCaseUid,
}: {
  open: boolean;
  onClose: () => void;
  mode: CloneMode;
  /** every application on this lead, oldest first — the same order `CaseSwitcher` numbers by */
  cases: JourneyCase[];
  /** START mode only */
  customerUid?: string;
  leadUid?: string;
  onStarted?: (caseUid: string) => void;
  /** COPY mode only — the application being copied INTO */
  targetCaseUid?: string;
}) => {
  const colors = useTheme().nawy.colors.semantic;
  const isStart = mode === "start";

  const [startOption, setStartOption] = useState<CloneStartOption>("empty");
  const [sourceCaseUid, setSourceCaseUid] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [overwriteConfirmed, setOverwriteConfirmed] = useState(false);
  const [overwriteRequired, setOverwriteRequired] = useState(false);
  const [results, setResults] = useState<StepCloneOutcome[] | null>(null);

  // §IdempotentStart: one uuid per dialog OPENING — stable across retries of the same submission
  // (the component itself never unmounts between opens, ResponsiveModal just hides it), fresh the
  // next time the agent opens this dialog. Only ever sent on a `start` submission.
  const idempotencyKeyRef = useRef<string | undefined>(undefined);
  // Re-seed default selection once per (source, preview) pair — not on every background refetch,
  // which would silently undo an agent's manual unticks.
  const seededForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    idempotencyKeyRef.current = crypto.randomUUID();
    setStartOption("empty");
    setSourceCaseUid(null);
    setSelected(new Set());
    setOverwriteConfirmed(false);
    setOverwriteRequired(false);
    setResults(null);
    seededForRef.current = null;
  }, [open]);

  const candidateSources = isStart ? cases : otherApplications(cases, targetCaseUid ?? null);
  const isCopying = !isStart || startOption === "copy";

  const sourcePreview = useCloneSourcePreview(isStart ? sourceCaseUid : null);
  const targetPreview = useCloneTargetPreview(isStart ? null : (targetCaseUid ?? null), isStart ? null : sourceCaseUid);
  const previewQuery = isStart ? sourcePreview : targetPreview;
  const rows: CloneStepPreview[] = previewQuery.data ?? [];
  const overwriteNeeded = !isStart && (needsOverwriteConfirm(rows, selected) || overwriteRequired);

  useEffect(() => {
    if (!previewQuery.data || !sourceCaseUid || seededForRef.current === sourceCaseUid) return;
    setSelected(defaultSelectedTemplateStepIds(previewQuery.data));
    seededForRef.current = sourceCaseUid;
  }, [previewQuery.data, sourceCaseUid]);

  const startWorkflow = useStartWorkflow();
  const createClones = useCreateStepClones();
  const busy = startWorkflow.create.isLoading || createClones.isLoading;

  const pickSource = (uid: string) => {
    setSourceCaseUid(uid);
    setSelected(new Set());
    setOverwriteConfirmed(false);
    setOverwriteRequired(false);
  };

  const toggleRow = (templateStepId: number) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (!next.delete(templateStepId)) next.add(templateStepId);
      return next;
    });
  };

  const cancel = () => {
    if (!busy) onClose();
  };

  // §CopyOverwrite409: the client-side check can be stale (another agent edited the target between
  // preview and submit) — the server's own refusal is the source of truth. Re-show the confirm band
  // and let the agent resend with `overwrite: true`; any other error is already toasted by the hook.
  const onCopyError = (error: unknown) => {
    if (!isCloneOverwriteRequired(error)) return;
    setOverwriteRequired(true);
    setOverwriteConfirmed(false);
  };

  const confirmStart = () => {
    if (!customerUid || !leadUid) return;
    startWorkflow.create.mutate(
      {
        customerUid,
        leadUid,
        cloneFrom: cloneFromForStart(isCopying, sourceCaseUid, selected),
        idempotencyKey: idempotencyKeyRef.current,
      },
      {
        onSuccess: (view) => {
          onStarted?.(view.case.uid);
          onClose();
        },
      },
    );
  };

  const confirmCopy = () => {
    if (!targetCaseUid || !sourceCaseUid) return;
    createClones.mutate(
      {
        targetCaseUid,
        sourceCaseUid,
        templateStepIds: [...selected],
        overwrite: overwriteNeeded || undefined,
      },
      { onSuccess: setResults, onError: onCopyError },
    );
  };

  const canConfirmStart = startOption === "empty" || !!sourceCaseUid;
  const canConfirmCopy = !!sourceCaseUid && selected.size > 0 && (!overwriteNeeded || overwriteConfirmed);

  const picker = (
    <Stack spacing={2}>
      {isStart && <StartOptionPicker value={startOption} disabled={busy} onChange={setStartOption} />}
      {isCopying && (
        <SourcePicker
          cases={cases}
          candidates={candidateSources}
          value={sourceCaseUid}
          disabled={busy}
          onPick={pickSource}
        />
      )}
      {isCopying && sourceCaseUid && (
        <Box>
          <Typography sx={labelSx(colors)}>Steps to copy</Typography>
          <CloneChecklist
            isLoading={previewQuery.isLoading}
            isError={previewQuery.isError}
            onRetry={() => previewQuery.refetch()}
            rows={rows}
            mode={mode}
            selected={selected}
            busy={busy}
            onToggle={toggleRow}
            overwriteNeeded={overwriteNeeded}
            overwriteConfirmed={overwriteConfirmed}
            onOverwriteConfirmedChange={setOverwriteConfirmed}
          />
        </Box>
      )}
    </Stack>
  );

  return (
    <ResponsiveModal
      open={open}
      onClose={cancel}
      title={isStart ? "Start another application" : "Copy data from another application"}
    >
      <Box
        sx={{
          width: { mobile: "100%", desktop: 560 },
          maxWidth: "100%",
          maxHeight: { mobile: "none", desktop: 480 },
          overflowY: { mobile: "visible", desktop: "auto" },
          px: { mobile: 0, tablet: 2.5 },
          py: { mobile: 0, tablet: 2 },
        }}
      >
        {results ? <CloneResults results={results} rows={rows} /> : picker}
      </Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{
          px: { mobile: 0, tablet: 2.5 },
          py: 2,
          borderTop: `1px solid ${colors.border.secondary}`,
        }}
      >
        <DialogFooter
          showDone={results !== null}
          busy={busy}
          confirmLabel={isStart ? "Start application" : "Copy steps"}
          confirmDisabled={busy || !(isStart ? canConfirmStart : canConfirmCopy)}
          onConfirm={isStart ? confirmStart : confirmCopy}
          onCancel={cancel}
          onDone={onClose}
        />
      </Stack>
    </ResponsiveModal>
  );
};

export default CloneStepsDialog;
